import { t, Trans } from '@lingui/macro';
import { Button } from '@we-make-websites/ui-lib';
import clsx from 'clsx';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { useEffectOnceWhen } from 'rooks';
import { useContext } from 'use-context-selector';
import { Breadcrumbs } from 'components/breadcrumbs/Breadcrumbs';
import { LoginFormContext } from 'components/loginForm/LoginFormContext';
import {
  FormCheckboxField,
  FormField,
  FormPasswordField,
} from 'components/signUpForm/FormFields';
import { sendDataToGTM } from 'utils/gtm/gtm';
import { AccountContentContext } from '../account/accountContentContext/AccountContentContext';
import { CustomerContext } from '../customerContext/CustomerContext';
import { StoreContext } from '../storeContext/StoreContext';
import styles from './loginForm.module.scss';

interface LoginFormInput {
  email: string;
  password: string;
  rememberMe: boolean;
}

export const LoginForm = () => {
  const { signedIn } = useContext(CustomerContext);

  const {
    customerSignIn,
    loading,
    errors: submissionErrors,
    success,
  } = useContext(LoginFormContext);

  const {
    login: { image },
  } = useContext(AccountContentContext);
  const { storeService } = useContext(StoreContext);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormInput>();

  const router = useRouter();

  const [isSubmitted, setIsSubmitted] = useState(false);

  const onSubmit: SubmitHandler<LoginFormInput> = (data) => {
    customerSignIn({
      email: data.email,
      password: data.password,
      // rememberMe: data.rememberMe,
    });

    setIsSubmitted(true);
  };

  /**
   * Route to account homepage when login is successful.
   */
  const isSuccessfulSubmission = isSubmitted && success;

  useEffectOnceWhen(() => {
    sendDataToGTM({ LoginState: 'logged-in' });
    storeService.send('CART_REINIT');
    router.push('/account');
  }, isSuccessfulSubmission || signedIn);

  return (
    <>
      <Breadcrumbs
        title={t({
          id: 'breadcrumbs.accountLogin',
          message: 'Account Log In',
        })}
      />
      <div className={clsx(styles.loginForm__wrapper, 'container grid')}>
        <div className={clsx(styles.loginForm, 'col l2-5 xs4')}>
          <h1 className="text-heading-h2-desktop text-heading-h2-mobile">
            <Trans id="page.login.heading">Account Log In</Trans>
          </h1>

          <p className="text-body-small-desktop text-body-small-mobile">
            <Trans id="page.login.subheading">
              Enter your email and password to log into your account.
            </Trans>
          </p>

          {submissionErrors && (
            <p
              className={clsx(
                styles.loginForm__error,
                'text-body-small-desktop text-body-small-mobile'
              )}
            >
              <Trans id="page.login.error">
                Login Failed: Your email or password is incorrect
              </Trans>
            </p>
          )}

          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <div className="form-group">
              <FormField
                type="email"
                id="loginEmail"
                error={errors.email}
                loading={loading}
                required
                label={t({
                  id: 'page.login.emailPlaceholder',
                  message: 'Email Address',
                })}
                register={() =>
                  register('email', {
                    required: t({
                      id: 'page.login.emailRequired',
                      message: 'Email is required',
                    }),
                  })
                }
              />

              <FormPasswordField
                id="loginPassword"
                error={errors.password}
                loading={loading}
                required
                label={t({
                  id: 'page.login.passwordPlaceholder',
                  message: 'Password',
                })}
                register={() =>
                  register('password', {
                    required: t({
                      id: 'page.login.passwordRequired',
                      message: 'Password is required',
                    }),
                  })
                }
              />
            </div>

            <div className={styles.loginForm__rememberWrapper}>
              <FormCheckboxField
                id="loginRememberMe"
                label={t({
                  id: 'page.login.rememberMe',
                  message: 'Remember Me',
                })}
                loading={loading}
                register={() => register('rememberMe')}
              />

              <Button
                type="button"
                modifiers="text"
                disabled={loading}
                href="/account/recover"
              >
                <span className="text-utility-utility-mobile text-utility-utility-desktop">
                  <Trans id="page.login.forgotPW">Forgot Password?</Trans>
                </span>
              </Button>
            </div>

            <div className={styles.loginForm__footer}>
              <Button type="submit" disabled={loading}>
                <span className="text-utility-utility-mobile text-utility-utility-desktop">
                  <Trans id="page.login.submit">Log In</Trans>
                </span>
              </Button>

              <div className={styles.loginForm__notMember}>
                <p className="text-body-2-desktop text-body-2-mobile">
                  <Trans id="page.login.notMember">Not a member?</Trans>
                </p>

                <Button
                  type="button"
                  modifiers="text"
                  disabled={loading}
                  href="/account/create"
                >
                  <span className="text-utility-utility-mobile text-utility-utility-desktop">
                    <Trans id="page.login.signUp">sign up</Trans>
                  </span>
                </Button>
              </div>
            </div>
          </form>
        </div>

        <div className={clsx(styles.loginForm__image, 'col l7-11 xs4')}>
          {image?.url && (
            <Image
              src={image.url}
              alt={image.title}
              title={image.title}
              fill
              sizes="(max-width: 1024px) 100vw, 522px"
            />
          )}
        </div>
      </div>
    </>
  );
};

export default LoginForm;
